// Página "Margem por setor": tarefa do Plano de ação (Ciclo 1 › Semana 1) e aba de
// Financeiro › DRE & Resultado. Lucro de cada setor mês a mês, na estrutura da DRE, no
// período escolhido (até 24 meses). A soma dos setores é igual à DRE em
// toda linha (o servidor confere e devolve a diferença; aqui ela aparece em vermelho).
// Rota: GET /financeiro/dre-setores (dre-engine). Contas puras em dadosSetores.ts.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, App, Button, Collapse, Modal, Popconfirm, Segmented, Space, Spin, Table, Tag, Tooltip as TipAntd, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { usePerms } from '../../../hooks/usePerms';
import { metaSetor } from '../../../utils/setoresDre';
import {
  aceitarSetorCusto, buscarDetalheSetor, buscarDreSetores, desfazerAceiteSetorCusto, recalcularFechamento, ErroDre,
  type Agrupar, type DetalheSetor, type DreSetores, type EscopoAceite, type LinhaMargem, type PropsPagina,
} from './apiDre';
import {
  avisosDosMeses, blocosDeMeses, categoriasAcumuladas, categoriasPorMes, calculadoEmMaisAntigo, chaveReceita, contagemPendencia, dataBr, itensDaPendencia, juntarBlocos,
  linhasDoGrafico, linhasMargem, margemPct, MAX_MESES_MARGEM, pctTexto, porSetorCanonico, PRIMEIRO_MES_RECALCULAVEL, reais, reaisCurto, rotuloItem,
  rotuloLinha, rotuloMes, rotuloMesCurto, rotuloRegra, setoresDaResposta, tendenciaTexto, totalMaoObraParada, totalMaoObraParadaNoRestante, totalSemSetor,
  valoresAcumulados, type MesSerie, type ModoMargem,
} from './dadosSetores';
import { nomesSetores, resumoVias, somarVias } from './dadosSetoresCusto';
import {
  AcessoNaoConferido, Carregando, COR_TENDENCIA, EtiquetaSetor, FiltroDePeriodo, FiltroDeSetor, FiltroDeTendencia, IrPara,
  LINK_AREAS, LINK_LANCAMENTOS, LINK_MAO_OBRA, SemAcesso, TooltipEmpilhado, TRACO_TENDENCIA, usePeriodo,
} from './comumDre';

const { Text } = Typography;
const PERM = 'financeiro.dre.ver';
const PERM_REVISAR = 'financeiro.setor_custo.revisar';   // aceitar/desfazer "segue o contrato"
const LINHAS_DE_LUCRO: { value: LinhaMargem; label: string }[] = [
  { value: 'lucro_bruto', label: 'Lucro bruto' },
  { value: 'resultado_op', label: 'Resultado operacional' },
  { value: 'lucro_liq', label: 'Lucro líquido' },
  { value: 'lucro_final', label: 'Lucro final' },
];

const PENDENCIAS: { chave: string; titulo: string; onde: React.ReactNode }[] = [
  {
    chave: 'parcelas_sem_setor', titulo: 'Parcelas sem setor',
    onde: <span>Corrija em <IrPara para={LINK_AREAS}>Operacional › Contratos › Áreas e CNAE</IrPara>, ou na parcela (Financeiro › Recebimentos › Contas a Receber › Detalhes › Rateio por setor).</span>,
  },
  {
    chave: 'manuais_sem_area', titulo: 'Receitas lançadas à mão sem área',
    onde: <span>Corrija em <IrPara para={LINK_LANCAMENTOS}>Financeiro › DRE & Resultado › Lançamentos</IrPara>.</span>,
  },
  {
    chave: 'contratos_sem_mix', titulo: 'Contratos com custo, mas sem nenhuma receita (o custo vai para “sem setor”)',
    onde: <span>Defina a divisão em <IrPara para={LINK_AREAS}>Operacional › Contratos › Áreas e CNAE</IrPara>.</span>,
  },
  {
    chave: 'contratos_mix_padrao', titulo: 'Contratos cuja divisão por setor não vem das parcelas (usamos o padrão, o faturamento antigo ou o rótulo)',
    onde: <span>Confira no contrato: Visão geral › cartão “Áreas e CNAE” (<IrPara para={LINK_AREAS}>lista</IrPara>).</span>,
  },
  {
    chave: 'custos_sem_setor', titulo: 'Custos sem setor',
    onde: <span>Pessoa: defina na <IrPara para="/administrativo/rh">ficha de RH</IrPara> (bloco Setores que a pessoa atende). Kit: <IrPara para="/operacional/kits">Operacional › Kits de equipamento</IrPara>. Lançamento e nota: revise na <IrPara para="/financeiro/dre-hub?aba=setores-custo">aba Setor dos custos</IrPara>. Sem setor, o custo segue a mistura do contrato.</span>,
  },
  {
    chave: 'custos_sem_casamento', titulo: 'Custo com setor que o contrato não tem',
    onde: <span>O setor do custo não aparece na receita do contrato, então o custo caiu na mistura do contrato. Ajuste o setor na <IrPara para="/administrativo/rh">ficha de RH</IrPara> (pessoa) ou em <IrPara para="/operacional/kits">Kits de equipamento</IrPara> (kit), ou a divisão do contrato em <IrPara para={LINK_AREAS}>Áreas e CNAE</IrPara>. Lançamento e nota: revise na <IrPara para="/financeiro/dre-hub?aba=setores-custo">aba Setor dos custos</IrPara>. Se está certo assim (ajuda pontual fora do setor da pessoa), use <strong>Aceitar: segue o contrato</strong> na linha, ou aceite o contrato inteiro — a Margem para de apontar depois do recálculo.</span>,
  },
];

const TIPO_ITEM: Record<string, string> = { pessoa: 'Pessoa', lancamento: 'Lançamento', bem: 'Kit/bem', nota: 'Nota' };
const num0 = (v: any) => Number(v) || 0;

const LARG_COL = 140;   // 260 + 140 × 8 colunas cabe num monitor de 1440 px sem rolagem lateral
const LARG_PRIMEIRA = 260;
const VIA_TEXTO: Record<string, string> = {
  item: 'setor do custo × receita do contrato',
  contrato: 'divisão do contrato',
  sem_setor: 'sem setor: divisão do contrato',
  sem_casamento: 'setor não casa: divisão do contrato',
  ficha_parado: 'sem hora anotada: setor da ficha da pessoa',
};
const ORIGEM_TEXTO: Record<string, string> = {
  mao_obra_horas: 'Mão de obra', mao_obra_clockify: 'Mão de obra', cc_lancamento: 'Lançamento',
  equipamento_checkin: 'Equipamento', combustivel_nota: 'Combustível', mao_obra_pendente: 'Mão de obra parada',
};
const tituloColuna = (n: React.ReactNode) => <div style={{ whiteSpace: 'normal', lineHeight: 1.2 }}>{n}</div>;
const pct1 = (f: number) => `${(f * 100).toFixed(1).replace('.', ',')}%`;

interface AlvoDetalhe { linha: string; categoria: string; setor: string; de: string; ate: string; agrupar: Agrupar; taxSource?: string }

/** "Como foi calculado": itens com custo direto no setor + o restante da linha repartido pela receita. */
function ModalComoCalculado({ alvo, onFechar }: { alvo: AlvoDetalhe | null; onFechar: () => void }) {
  const [det, setDet] = useState<DetalheSetor | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [tentativa, setTentativa] = useState(0);
  useEffect(() => {
    if (!alvo) { setDet(null); setErro(null); return; }
    let vivo = true;
    setDet(null); setErro(null); setCarregando(true);
    buscarDetalheSetor({ de: alvo.de, ate: alvo.ate, agrupar: alvo.agrupar, tax_source: alvo.taxSource, linha: alvo.linha, categoria: alvo.categoria, setor: alvo.setor })
      .then((r) => { if (vivo) setDet(r); })
      .catch((e: any) => { if (vivo) setErro(String(e?.message || e)); })
      .finally(() => { if (vivo) setCarregando(false); });
    return () => { vivo = false; };
  }, [alvo, tentativa]);

  const periodo = alvo ? (alvo.de === alvo.ate ? rotuloMes(alvo.de) : `${rotuloMes(alvo.de)} a ${rotuloMes(alvo.ate)}`) : '';
  const titulo = alvo ? `Como foi calculado — ${alvo.categoria} · ${metaSetor(alvo.setor).rotulo} · ${periodo}` : '';
  const itens = useMemo(() => (det ? [...det.itens].sort((a, b) => a.valor_setor - b.valor_setor) : []), [det]);

  return (
    <Modal
      open={!!alvo} onCancel={onFechar} footer={<Button onClick={onFechar}>Fechar</Button>} title={titulo}
      width="94vw" style={{ maxWidth: 1400, top: 16 }} styles={{ body: { maxHeight: 'calc(100vh - 130px)', overflowY: 'auto' } }}
      destroyOnHidden
    >
      {carregando ? <div style={{ textAlign: 'center', padding: 40 }}><Spin tip="Buscando os itens..."><div style={{ padding: 20 }} /></Spin></div> : null}
      {erro ? (
        <Alert type="error" showIcon message="Não foi possível abrir o detalhe" description={erro}
          action={<Button size="small" onClick={() => setTentativa((t) => t + 1)}>Tentar de novo</Button>} />
      ) : null}
      {det ? (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Text strong>
            Total no setor: {reais(det.total)} = custo com setor {reais(det.soma_itens)} ({det.n_itens} {det.n_itens === 1 ? 'item' : 'itens'}) + restante da linha repartido pela receita {reais(det.soma_rateio)}
          </Text>
          {det.truncado ? (
            <Alert type="warning" showIcon message={`Mostrando os ${det.itens.length} primeiros itens de ${det.n_itens}.`} />
          ) : null}
          <div>
            <Text strong>Custo com setor (contrato, ou a ficha da pessoa quando ela não anotou hora)</Text>
            <Table
              style={{ marginTop: 8 }}
              size="small" rowKey={(_, i) => String(i)} dataSource={itens} pagination={{ pageSize: 50, hideOnSinglePage: true }}
              scroll={{ x: 'max-content' }}
              locale={{ emptyText: 'Nenhum item nesta célula pelo contrato ou pela ficha.' }}
              columns={[
                { title: 'Mês', dataIndex: 'competencia', render: (v: string) => rotuloMesCurto(v) },
                { title: 'Contrato', render: (_: any, r: any) => (r.contrato ? <TipAntd title={r.titulo || undefined}><span>{r.contrato}</span></TipAntd> : '—') },
                { title: 'Quem', render: (_: any, r: any) => r.quem?.nome || '—' },
                { title: 'Origem', dataIndex: 'origem', render: (v: string) => ORIGEM_TEXTO[v] || v },
                { title: 'Valor do item', align: 'right' as const, render: (_: any, r: any) => <span style={{ color: corNumero(r.valor) }}>{reais(r.valor)}</span> },
                { title: 'Como entrou', dataIndex: 'via', render: (v: string) => VIA_TEXTO[v] || v },
                { title: 'Peso', align: 'right' as const, render: (_: any, r: any) => pct1(r.peso) },
                { title: 'Valor no setor', align: 'right' as const, render: (_: any, r: any) => <span style={{ color: corNumero(r.valor_setor) }}>{reais(r.valor_setor)}</span> },
              ]}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={7}><Text strong>Soma dos itens</Text></Table.Summary.Cell>
                  <Table.Summary.Cell index={7} align="right"><Text strong>{reais(det.soma_itens)}</Text></Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          </div>
          <div>
            <Text strong>Restante da linha, repartido pela receita</Text>
            <Table
              style={{ marginTop: 8 }}
              size="small" rowKey="competencia" dataSource={det.rateio} pagination={false} scroll={{ x: 'max-content' }}
              locale={{ emptyText: 'Sem rateio neste período.' }}
              columns={[
                { title: 'Mês', dataIndex: 'competencia', render: (v: string) => rotuloMesCurto(v) },
                { title: 'Valor da categoria na DRE', align: 'right' as const, render: (_: any, r: any) => reais(r.dre) },
                { title: 'Custo direto já repartido', align: 'right' as const, render: (_: any, r: any) => reais(r.diretos) },
                { title: 'Parado pela ficha', align: 'right' as const, render: (_: any, r: any) => (r.parado_ficha == null || Math.abs(r.parado_ficha) < 0.005 ? '' : reais(r.parado_ficha)) },
                { title: 'Restante', align: 'right' as const, render: (_: any, r: any) => reais(r.restante) },
                {
                  title: 'Peso da receita do setor no mês', align: 'right' as const,
                  render: (_: any, r: any) => `${pct1(r.peso)}${r.peso_origem === 'janela' ? ' (receita da janela)' : r.peso_origem === 'sem_receita' ? ' (sem receita: tudo em Sem setor)' : ''}`,
                },
                { title: 'Valor no setor', align: 'right' as const, render: (_: any, r: any) => <span style={{ color: corNumero(r.valor_setor) }}>{reais(r.valor_setor)}</span> },
              ]}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={6}><Text strong>Soma do rateio</Text></Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="right"><Text strong>{reais(det.soma_rateio)}</Text></Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          </div>
          {det.origem_restante && det.origem_restante.length > 0 ? (
            <div>
              <Text strong>De onde vem o restante</Text>
              <Table
                style={{ marginTop: 8 }}
                size="small" rowKey={(_, i) => String(i)} dataSource={det.origem_restante}
                pagination={{ pageSize: 50, hideOnSinglePage: true }} scroll={{ x: 1000 }}
                columns={[
                  { title: 'Mês', dataIndex: 'competencia', render: (v: string) => rotuloMesCurto(v) },
                  {
                    title: 'Quem', dataIndex: 'quem', width: 230,
                    render: (v: string, r: any) => (r.tipo === 'ajuste' ? <Text type="secondary" italic>{v}</Text> : v),
                  },
                  { title: 'Na DRE', align: 'right' as const, render: (_: any, r: any) => (r.na_dre == null ? '' : reais(r.na_dre)) },
                  { title: 'Em contratos', align: 'right' as const, render: (_: any, r: any) => (r.em_contratos == null ? '' : reais(r.em_contratos)) },
                  { title: 'Horas internas (despesa)', align: 'right' as const, render: (_: any, r: any) => (r.em_despesas == null ? '' : reais(r.em_despesas)) },
                  { title: 'No setor da ficha (sem hora)', align: 'right' as const, render: (_: any, r: any) => (r.no_setor == null || Math.abs(r.no_setor) < 0.005 ? '' : reais(r.no_setor)) },
                  { title: 'Restante', align: 'right' as const, render: (_: any, r: any) => <span style={{ color: corNumero(r.restante) }}>{reais(r.restante)}</span> },
                  { title: 'Por quê', dataIndex: 'motivo', width: 230, render: (v: string | null) => v || '' },
                ]}
                summary={() => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={6}><Text strong>Soma dos restantes</Text></Table.Summary.Cell>
                    <Table.Summary.Cell index={6} align="right"><Text strong>{reais(det.soma_rateio_restante ?? 0)}</Text></Table.Summary.Cell>
                    <Table.Summary.Cell index={7} />
                  </Table.Summary.Row>
                )}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Restante = o que ficou na DRE desta categoria sem ir para contrato, para despesa por horas internas ou para o setor da ficha (quem recebeu sem anotar hora). Em mês com a regra antiga (ainda não recalculado) a mão de obra ia ao contrato pelo custo da hora anotada — e podia passar do que a pessoa recebeu.
              </Text>
            </div>
          ) : null}
          <Text type="secondary" style={{ fontSize: 12 }}>A soma das duas tabelas é o valor da célula. Itens com peso zero neste setor não aparecem.</Text>
        </Space>
      ) : null}
    </Modal>
  );
}

/** Tabela de itens das pendências de setor do custo. */
function TabelaItensSetorCusto({ itens, podeAceitar, onAceitar }: { itens: any[]; podeAceitar: boolean; onAceitar: (it: any, escopo: EscopoAceite) => void }) {
  return (
    <Table
      size="small" pagination={false} rowKey={(_, i) => String(i)} scroll={{ x: 'max-content' }}
      dataSource={itens.slice(0, 15)}
      columns={[
        { title: 'Tipo', key: 'tipo', render: (_: any, it: any) => TIPO_ITEM[it.tipo] || it.tipo || '—' },
        {
          title: 'Nome', key: 'nome',
          render: (_: any, it: any) => (it.tipo === 'pessoa' && it.ref
            ? <IrPara para={`/administrativo/rh?pessoa=${encodeURIComponent(String(it.ref))}&aba=rateio`}>{it.nome || it.ref}</IrPara>
            : (it.nome || it.ref || '—')),
        },
        { title: 'Contrato', key: 'contrato', render: (_: any, it: any) => it.numero || it.contrato_id || '—' },
        { title: 'Linha', dataIndex: 'linha', key: 'linha', render: (v: string) => (v ? rotuloLinha(v) : '—') },
        { title: 'Setor do custo', key: 'item', render: (_: any, it: any) => nomesSetores(it.setores_item) },
        { title: 'Mistura do contrato', key: 'mix', render: (_: any, it: any) => nomesSetores(it.mix) },
        { title: 'Valor', key: 'valor', align: 'right' as const, render: (_: any, it: any) => reais(num0(it.valor)) },
        { title: 'Meses', key: 'meses', render: (_: any, it: any) => (Array.isArray(it.meses) ? it.meses.map(rotuloMesCurto).join(', ') : '—') },
        {
          title: 'Aceite ou revisão', key: 'aceite',
          render: (_: any, it: any) => {
            if (it.aceito) {
              // Mês congelado lê o aceite na hora (ficha de hoje); só pede recálculo se algum mês recalcula.
              const pedeRecalculo = !Array.isArray(it.meses) || !it.meses.length || it.meses.some((m: string) => m >= PRIMEIRO_MES_RECALCULAVEL);
              return <Tag color="cyan">Aceito{it.aceito.escopo === 'contrato' ? ' (contrato inteiro)' : ''}{it.aceito.por ? ` por ${it.aceito.por}` : ''}{pedeRecalculo ? ' — recalcule' : ''}</Tag>;
            }
            if (it.tipo === 'lancamento' || it.tipo === 'nota') return <Text type="secondary">revise na aba Setor dos custos</Text>;
            if (!it.ref || !it.contrato_id) return null;
            return (
              <Space size={4} wrap>
                <Popconfirm
                  title={`Aceitar: no ${it.numero || 'contrato'}, o custo de ${it.nome || it.ref} que não casa com a receita segue a divisão do contrato?`}
                  okText="Aceitar" cancelText="Cancelar" onConfirm={() => onAceitar(it, it.tipo)} disabled={!podeAceitar}>
                  <Button size="small" type="primary" disabled={!podeAceitar}>Aceitar: segue o contrato</Button>
                </Popconfirm>
                <Popconfirm
                  title={`No ${it.numero || 'contrato'}, todo custo cujo setor o contrato não vende passa a seguir a divisão do contrato (o que já casa não muda; marcação manual vence)?`}
                  okText="Aceitar o contrato inteiro" cancelText="Cancelar" onConfirm={() => onAceitar(it, 'contrato')} disabled={!podeAceitar}>
                  <Button size="small" disabled={!podeAceitar}>todo o contrato</Button>
                </Popconfirm>
              </Space>
            );
          },
        },
      ]}
    />
  );
}

/** Os aceites registrados ("segue o contrato"), com Desfazer. */
function TabelaAceites({ aceites, podeAceitar, onDesfazer }: { aceites: any[]; podeAceitar: boolean; onDesfazer: (id: number) => void }) {
  const ESCOPO: Record<string, string> = { contrato: 'contrato inteiro', pessoa: 'pessoa', bem: 'equipamento', kit: 'kit' };
  return (
    <Table
      size="small" pagination={false} rowKey={(a: any) => String(a.id)} scroll={{ x: 'max-content' }}
      dataSource={aceites}
      columns={[
        { title: 'Contrato', key: 'contrato', render: (_: any, a: any) => a.numero || a.contrato_id },
        { title: 'O que segue o contrato', key: 'o_que', render: (_: any, a: any) => (a.escopo === 'contrato' ? 'Todo custo do contrato' : `${ESCOPO[a.escopo] || a.escopo}: ${a.nome || a.ref}`) },
        { title: 'Por', dataIndex: 'por', key: 'por' },
        { title: 'Em', key: 'em', render: (_: any, a: any) => (a.em ? dataBr(String(a.em).slice(0, 10)) : '—') },
        {
          title: '', key: 'acao',
          render: (_: any, a: any) => (
            <Popconfirm title="Desfazer este aceite? O custo volta a ser apontado depois do recálculo." okText="Desfazer" cancelText="Cancelar" onConfirm={() => onDesfazer(Number(a.id))} disabled={!podeAceitar}>
              <Button size="small" disabled={!podeAceitar}>Desfazer</Button>
            </Popconfirm>
          ),
        },
      ]}
    />
  );
}

const corNumero = (v: number) => (v < -0.005 ? '#a8071a' : undefined);

export default function MargemSetor({ podeVer = true, hoje: hojeProp }: PropsPagina) {
  const { pode, carregando: carregandoPerms, erro: erroPerms, recarregar } = usePerms();
  const { message } = App.useApp();
  const liberado = podeVer && pode(PERM);
  const podeAceitar = pode(PERM_REVISAR);
  const { hoje, filtro, setFiltro, janela } = usePeriodo(hojeProp, MAX_MESES_MARGEM);
  const de = janela.ok ? janela.de : null;
  const ate = janela.ok ? janela.ate : null;
  const [setor, setSetor] = useState<string | null>(null);
  /** null = automático: ligada com um setor escolhido, desligada com "Todos". */
  const [tendenciaManual, setTendenciaManual] = useState<boolean | null>(null);

  const [linha, setLinha] = useState<LinhaMargem>('lucro_liq');
  const [modo, setModo] = useState<ModoMargem>('mes');
  const [agrupar, setAgrupar] = useState<Agrupar>('frentes');
  const [dados, setDados] = useState<DreSetores | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [progresso, setProgresso] = useState<string | null>(null);
  const [erro, setErro] = useState<ErroDre | null>(null);
  // Recalcular: andamento {feitos, total} enquanto roda; null parado.
  const [recalc, setRecalc] = useState<{ feitos: number; total: number } | null>(null);
  const pedido = useRef(0);
  const [abertas, setAbertas] = useState<React.Key[]>([]);
  const [alvo, setAlvo] = useState<AlvoDetalhe | null>(null);

  const carregar = useCallback(async () => {
    if (!de || !ate) return;
    const meu = ++pedido.current;
    setCarregando(true); setErro(null); setProgresso(null);
    try {
      let resp: DreSetores;
      try {
        resp = await buscarDreSetores({ de, ate, agrupar });
      } catch (e: any) {
        // Muitas DREs de uma vez podem passar do tempo da conexão: pede em blocos de 3 meses.
        const lento = e instanceof ErroDre && (e.status == null || e.status >= 500);
        if (!lento) throw e;
        const blocos = blocosDeMeses(de, ate, 3);
        const partes: DreSetores[] = [];
        for (let i = 0; i < blocos.length; i++) {
          if (meu !== pedido.current) return;
          setProgresso(`Calculando em partes (${i + 1} de ${blocos.length})...`);
          partes.push(await buscarDreSetores({ ...blocos[i], agrupar }));
        }
        resp = juntarBlocos(partes);
      }
      if (meu === pedido.current) setDados(resp);
    } catch (e: any) {
      if (meu === pedido.current) setErro(e instanceof ErroDre ? e : new ErroDre(String(e?.message || e), null));
    } finally {
      if (meu === pedido.current) { setCarregando(false); setProgresso(null); }
    }
  }, [agrupar, de, ate]);

  useEffect(() => { if (liberado) carregar(); }, [liberado, carregar]);

  const avisos = useMemo(() => (dados ? avisosDosMeses(dados.meses) : null), [dados]);
  const chaveRec = useMemo(() => (dados ? chaveReceita(dados.linhas) : 'receita_bruta'), [dados]);

  // Setores que aparecem: os que têm algum valor em alguma linha do período.
  const setores = useMemo(() => {
    if (!dados) return [];
    const mapas = dados.linhas.map((l) => valoresAcumulados(dados, l.chave));
    const todos = setoresDaResposta(dados.setores, mapas);
    return todos.filter((s) => mapas.some((m) => Math.abs(m[s] || 0) >= 0.5));
  }, [dados]);

  const grafico = useMemo(() => (dados ? linhasMargem(dados, linha, setores, modo) : []), [dados, linha, setores, modo]);
  // Setor que não existe no agrupamento atual (trocou 5 frentes ↔ 6 setores) vale "Todos".
  const setorEf = setor && setores.includes(setor) ? setor : null;
  const comTendencia = (tendenciaManual ?? setorEf != null) && modo === 'mes';
  const foco = useMemo(() => {
    const serie: MesSerie[] = grafico.map((g) => ({
      periodo: g.periodo, parcial: g.parcial, total: g.total,
      porSetor: Object.fromEntries(setores.map((s) => [s, Number(g[s]) || 0])),
    }));
    const r = linhasDoGrafico(serie, setores, setorEf, comTendencia);
    if (modo === 'acumulado') r.linhas.forEach((l) => { l.label = 'Período'; });
    return r;
  }, [grafico, setores, setorEf, comTendencia, modo]);

  // Com um setor escolhido: a DRE dele mês a mês (linhas da DRE × meses + o período).
  const linhasDoSetor = useMemo(() => {
    if (!dados || !setorEf) return [];
    const doMes = (m: { valores: Record<string, any> }, chave: string) => porSetorCanonico(m.valores?.[chave])[setorEf] || 0;
    const recMes = Object.fromEntries(dados.meses.map((m) => [m.periodo, doMes(m, chaveRec)]));
    const recPeriodo = valoresAcumulados(dados, chaveRec)[setorEf] || 0;
    const rows: any[] = [];
    dados.linhas.forEach((l) => {
      const v: Record<string, number> = Object.fromEntries(dados.meses.map((m) => [m.periodo, doMes(m, l.chave)]));
      v.periodo = valoresAcumulados(dados, l.chave)[setorEf] || 0;
      const ehLucro = LINHAS_DE_LUCRO.some((x) => x.value === l.chave);
      const destaque = ehLucro || /sub|total|resultado/.test(l.tipo || '') || l.chave === chaveRec;
      const cats = l.tipo === 'grupo' ? categoriasPorMes(dados.meses, l.chave, setorEf) : {};
      const filhas = Object.entries(cats)
        .sort((a, b) => Math.abs(b[1].periodo) - Math.abs(a[1].periodo))
        .map(([cat, vals]) => ({ key: `${l.chave}::${cat}`, rotulo: cat, valores: vals, destaque: false, margem: false, categoria: { grupo: l.chave, nome: cat } }));
      rows.push({ key: l.chave, rotulo: rotuloLinha(l.chave, l.rotulo), valores: v, destaque, margem: false, ...(filhas.length ? { children: filhas } : {}) });
      if (ehLucro) {
        const mg: Record<string, number | null> = { periodo: margemPct(v.periodo, recPeriodo) };
        dados.meses.forEach((m) => { mg[m.periodo] = margemPct(v[m.periodo], recMes[m.periodo]); });
        rows.push({ key: `${l.chave}_pct`, rotulo: `Margem do ${rotuloLinha(l.chave, l.rotulo).toLowerCase()} (sobre a receita bruta)`, valores: mg, destaque: false, margem: true });
      }
    });
    return rows;
  }, [dados, setorEf, chaveRec]);

  const linhasTabela = useMemo(() => {
    if (!dados) return [];
    const rec = valoresAcumulados(dados, chaveRec);
    const rows: any[] = [];
    dados.linhas.forEach((l) => {
      const v = valoresAcumulados(dados, l.chave);
      const destaque = LINHAS_DE_LUCRO.some((x) => x.value === l.chave) || /sub|total|resultado/.test(l.tipo || '') || l.chave === chaveRec;
      const cats = l.tipo === 'grupo' ? categoriasAcumuladas(dados.meses, l.chave) : {};
      const filhas = Object.entries(cats)
        .sort((a, b) => Math.abs(b[1].total || 0) - Math.abs(a[1].total || 0))
        .map(([cat, vals]) => ({ key: `${l.chave}::${cat}`, rotulo: cat, valores: vals, destaque: false, margem: false, categoria: { grupo: l.chave, nome: cat } }));
      rows.push({ key: l.chave, rotulo: rotuloLinha(l.chave, l.rotulo), valores: v, destaque, margem: false, ...(filhas.length ? { children: filhas } : {}) });
      if (LINHAS_DE_LUCRO.some((x) => x.value === l.chave)) {
        const m: Record<string, number | null> = { total: margemPct(v.total || 0, rec.total || 0) };
        setores.forEach((s) => { m[s] = margemPct(v[s] || 0, rec[s] || 0); });
        rows.push({ key: `${l.chave}_pct`, rotulo: `Margem do ${rotuloLinha(l.chave, l.rotulo).toLowerCase()} (sobre a receita bruta)`, valores: m, destaque: false, margem: true });
      }
    });
    return rows;
  }, [dados, chaveRec, setores]);

  async function recalcular() {
    if (!avisos || recalc) return;
    // TODOS os meses pendentes, um por vez (cada mês roda a DRE geral inteira, alguns
    // segundos), com o andamento no botão. Até 14/10/2026 eram 6 por clique e a
    // mensagem dizia "Custos recalculados" como se tivesse acabado: o Lucas aceitou
    // 7 contratos, clicou uma vez, e a lista "não limpava" — jul–out ficaram para trás.
    const lista = avisos.recalculaveis.slice();
    const falhas: string[] = [];
    let feitos = 0;
    setRecalc({ feitos: 0, total: lista.length });
    for (const p of lista) {
      try { await recalcularFechamento(p); feitos++; } catch (e: any) { falhas.push(`${rotuloMes(p)}: ${e.message}`); }
      setRecalc({ feitos: feitos + falhas.length, total: lista.length });
    }
    setRecalc(null);
    const feitosTxt = `${feitos} ${feitos === 1 ? 'mês recalculado' : 'meses recalculados'}`;
    if (!falhas.length) message.success(`${feitosTxt}. A lista de conferência já reflete os aceites e as revisões.`, 6);
    else if (feitos) message.warning(`${feitosTxt} de ${lista.length}. Não calculou: ${falhas.join(' · ')}`, 10);
    else message.error('Não calculou: ' + falhas.join(' · '), 10);
    carregar();
  }

  if (carregandoPerms) return <Carregando />;
  if (!liberado) return podeVer && erroPerms ? <AcessoNaoConferido onTentar={recarregar} /> : <SemAcesso />;

  const mesesFechados = foco.linhas.filter((l) => !l.parcial).length;
  const controles = (
    <Space wrap size={[16, 8]} style={{ marginBottom: 12 }}>
      <FiltroDePeriodo filtro={filtro} onChange={setFiltro} hoje={hoje} janela={janela} />
      <FiltroDeSetor setores={setores} valor={setorEf} onChange={(s) => { setSetor(s); setTendenciaManual(null); }} />
      <FiltroDeTendencia
        ligado={comTendencia} onChange={setTendenciaManual}
        desligadoPorque={modo === 'acumulado' ? 'No acumulado o período vira uma barra só.' : dados && mesesFechados < 2 ? 'Precisa de pelo menos 2 meses fechados no período.' : null}
      />
      <Segmented value={linha} onChange={(v) => setLinha(v as LinhaMargem)} options={LINHAS_DE_LUCRO} />
      <Segmented value={modo} onChange={(v) => setModo(v as ModoMargem)} options={[{ value: 'mes', label: 'Mês a mês' }, { value: 'acumulado', label: 'Acumulado' }]} />
      <Segmented value={agrupar} onChange={(v) => setAgrupar(v as Agrupar)} options={[{ value: 'frentes', label: '5 frentes' }, { value: 'setores', label: '6 setores' }]} />
    </Space>
  );
  const avisoJanela = janela.ok
    ? (janela.aviso ? <Alert type="info" showIcon style={{ marginBottom: 12 }} message={janela.aviso} /> : null)
    : <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={janela.aviso} />;

  if (!janela.ok) return <div>{controles}{avisoJanela}</div>;
  if (erro && !dados) {
    return (
      <div>
        {controles}{avisoJanela}
        {erro.status === 403 ? <SemAcesso /> : (
          <Alert type="error" showIcon message="Não foi possível calcular a margem por setor" description={erro.message}
            action={<Button size="small" onClick={carregar}>Tentar de novo</Button>} />
        )}
      </div>
    );
  }
  if (!dados || !avisos) return <div>{controles}{avisoJanela}<Carregando texto={progresso || 'Calculando a DRE de cada mês, pode levar alguns segundos...'} /></div>;

  const semSetor = totalSemSetor(dados.meses, chaveRec);
  const maoObra = totalMaoObraParada(dados.meses);
  const maoObraNoRestante = totalMaoObraParadaNoRestante(dados.meses);
  const calculadoEm = dataBr(calculadoEmMaisAntigo(dados.meses));
  // 14/10/2026: nos meses congelados o setor vem da ficha de hoje, em memória (o servidor diz de onde veio).
  const fichaHoje = dados.meses.some((m) => m.fechamento?.setores_fonte === 'ficha_hoje');
  const lista = (ps: string[]) => ps.map(rotuloMes).join(', ');
  const rotuloLinhaAtual = LINHAS_DE_LUCRO.find((x) => x.value === linha)?.label || linha;
  const pendenciasComItem = PENDENCIAS.filter((p) => contagemPendencia(dados.pendencias?.[p.chave]) > 0);
  const aceitesRegistrados: any[] = Array.isArray((dados.pendencias as any)?.aceites) ? (dados.pendencias as any).aceites : [];
  const aceitar = async (it: any, escopo: EscopoAceite) => {
    try {
      const r = await aceitarSetorCusto({ contrato_id: String(it.contrato_id), escopo, ref: escopo === 'contrato' ? null : String(it.ref) });
      message.success(r.ja_existia ? 'Esse aceite já existia.' : 'Aceite registrado. Recalcule os meses do contrato para a lista atualizar.', 6);
      await carregar();
    } catch (e: any) { message.error(e.message || 'Não consegui registrar o aceite.'); }
  };
  const desfazerAceite = async (id: number) => {
    try {
      await desfazerAceiteSetorCusto(id);
      message.success('Aceite desfeito. Recalcule os meses do contrato.', 6);
      await carregar();
    } catch (e: any) { message.error(e.message || 'Não consegui desfazer.'); }
  };

  const abrirDetalhe = (r: any, setorCol: string, de1: string, ate1: string) => setAlvo({
    linha: r.categoria.grupo, categoria: r.categoria.nome, setor: setorCol, de: de1, ate: ate1, agrupar, taxSource: dados.tax_source,
  });
  const numeroClicavel = (r: any, v: number, setorCol: string, de1: string, ate1: string, forte = false) => (
    r.categoria && Math.abs(v) >= 0.005
      ? (
        <Button type="link" size="small" style={{ padding: 0, height: 'auto', color: corNumero(v), fontWeight: forte ? 600 : undefined }}
          onClick={() => abrirDetalhe(r, setorCol, de1, ate1)}>{reais(v)}</Button>
      )
      : <span style={{ color: corNumero(v) }}>{reais(v)}</span>
  );
  const temCategorias = dados.meses.some((m) => m.categorias && Object.keys(m.categorias).length > 0);
  const chavesAbrir = (setorEf ? linhasDoSetor : linhasTabela).filter((r: any) => r.children?.length).map((r: any) => r.key);
  const botaoCategorias = temCategorias && chavesAbrir.length ? (
    <Button size="small" style={{ margin: '8px 0' }} onClick={() => setAbertas(abertas.length ? [] : chavesAbrir)}>
      {abertas.length ? 'Fechar categorias' : 'Abrir categorias'}
    </Button>
  ) : null;

  const colunas = [
    { title: 'Linha da DRE', dataIndex: 'rotulo', key: 'rotulo', fixed: 'left' as const, width: LARG_PRIMEIRA },
    ...setores.map((s) => ({
      title: tituloColuna(<EtiquetaSetor chave={s} quebrar />), key: s, align: 'right' as const, width: LARG_COL,
      render: (_: any, r: any) => {
        const v = r.valores[s];
        if (v == null) return r.margem ? '—' : '';
        if (r.categoria) return numeroClicavel(r, v, s, dados.de, dados.ate);
        return <span style={{ color: corNumero(v) }}>{r.margem ? pctTexto(v) : reais(v)}</span>;
      },
    })),
    {
      title: 'Total (= DRE)', key: 'total', align: 'right' as const, width: LARG_COL,
      render: (_: any, r: any) => {
        const v = r.valores.total;
        return <Text strong style={{ color: corNumero(v || 0) }}>{r.margem ? pctTexto(v) : reais(v || 0)}</Text>;
      },
    },
  ];

  const rotuloSetor = setorEf ? metaSetor(setorEf).rotulo : null;
  const tendencia = foco.tendencia;
  const nSeries = (setorEf ? 1 : setores.length + 1) + (tendencia ? 1 : 0);
  const colunasDoSetor = [
    { title: 'Linha da DRE', dataIndex: 'rotulo', key: 'rotulo', fixed: 'left' as const, width: LARG_PRIMEIRA },
    ...dados.meses.map((m) => ({
      title: tituloColuna(rotuloMesCurto(m.periodo) + (m.parcial ? '*' : '')), key: m.periodo, align: 'right' as const, width: LARG_COL,
      render: (_: any, r: any) => {
        const v = r.valores[m.periodo];
        if (v == null) return r.margem ? '—' : '';
        if (r.categoria && setorEf) return numeroClicavel(r, v, setorEf, m.periodo, m.periodo);
        return <span style={{ color: corNumero(v) }}>{r.margem ? pctTexto(v) : reais(v)}</span>;
      },
    })),
    {
      title: 'Período', key: 'periodo', align: 'right' as const, width: LARG_COL,
      render: (_: any, r: any) => {
        const v = r.valores.periodo;
        if (v == null) return r.margem ? '—' : '';
        if (r.categoria && setorEf) return numeroClicavel(r, v, setorEf, dados.de, dados.ate, true);
        return <Text strong style={{ color: corNumero(v) }}>{r.margem ? pctTexto(v) : reais(v)}</Text>;
      },
    },
  ];

  return (
    <div>
      {controles}
      {avisoJanela}

      <Space direction="vertical" size={8} style={{ width: '100%', marginBottom: 12 }}>
        <Text type="secondary">
          {rotuloMes(dados.de)} a {rotuloMes(dados.ate)}. Custos de contrato vão para o setor pela receita do contrato na vida toda; o resto (administrativo, comercial, financeiro, impostos) é dividido pela receita do setor no mês.
          {calculadoEm ? ` Divisão dos custos calculada em ${calculadoEm}.` : ''}
        </Text>
        {avisos.conferenciaErrada.length ? (
          <Alert
            type="error" showIcon
            message="A soma dos setores não bate com a DRE"
            description={avisos.conferenciaErrada.map((c) => `${rotuloMes(c.periodo)}: diferença de ${reais(c.diferenca)}`).join(' · ')}
          />
        ) : null}
        {avisos.recalculaveis.length ? (
          <Alert
            type="warning" showIcon
            message={`Custos de ${lista(avisos.recalculaveis)} estão desatualizados`}
            description="A soma continua fechando com a DRE, mas a divisão dos custos por contrato foi calculada antes da regra de hoje (ou ainda não foi calculada)."
            action={(
              <Button size="small" icon={<ReloadOutlined />} loading={!!recalc} onClick={recalcular}>
                {recalc ? `Recalculando ${Math.min(recalc.feitos + 1, recalc.total)} de ${recalc.total}…`
                  : `Recalcular ${avisos.recalculaveis.length} ${avisos.recalculaveis.length === 1 ? 'mês' : 'meses'}`}
              </Button>
            )}
          />
        ) : null}
        {avisos.setoresDesatualizados.length ? (
          <Text type="secondary">{rotuloRegra('setores_desatualizados')}: {lista(avisos.setoresDesatualizados)}.</Text>
        ) : null}
        {resumoVias(somarVias(dados.meses.map((m) => m.vias))) ? (
          <Text type="secondary">
            Custo direto no período: {resumoVias(somarVias(dados.meses.map((m) => m.vias)))}.
          </Text>
        ) : null}
        {avisos.congeladas.length || avisos.antigas.some((p) => !avisos.recalculaveis.includes(p)) ? (
          <Alert
            type="info" showIcon
            message={`Meses antes de ${rotuloMes(PRIMEIRO_MES_RECALCULAVEL)} usam a regra antiga de mão de obra: ${lista([...avisos.congeladas, ...avisos.antigas.filter((p) => !avisos.congeladas.includes(p) && !avisos.recalculaveis.includes(p))])}`}
            description={`Esses meses estão congelados e não podem ser recalculados. A margem deles só vale como ordem de grandeza.${fichaHoje
              ? ' O setor de cada custo neles vem da ficha de hoje (pessoa, kit, lançamento revisado); o que não tem dono segue a divisão do contrato. Aceites valem na hora, sem recalcular.'
              : ''}`}
          />
        ) : null}
        {avisos.abertos.length ? (
          <Alert
            type="info" showIcon
            message={`${lista(avisos.abertos)} ainda está em andamento`}
            description="A despesa já entra no começo do mês e a receita só quando a nota é emitida, então o lucro deste mês aparece menor do que será."
          />
        ) : null}
        {Math.abs(semSetor) >= 0.01 ? (
          <Alert
            type="warning" showIcon
            message={`${reais(semSetor)} de receita sem setor`}
            description={<span>Aparece na coluna “Sem setor”. Para corrigir: <IrPara para={LINK_AREAS}>Operacional › Contratos › Áreas e CNAE</IrPara>.</span>}
          />
        ) : null}
        {Math.abs(maoObra) >= 0.01 ? (   // o parado vem negativo (despesa): até 15/10/2026 este aviso nunca aparecia

          <Alert
            type="info" showIcon
            message={`${reais(Math.abs(maoObra))} de mão de obra parada (paga sem hora anotada em contrato)`}
            description={<span>{Math.abs(maoObra) - Math.abs(maoObraNoRestante) >= 0.01 ? 'Vai para o setor da ficha de cada pessoa.' : ''}{Math.abs(maoObraNoRestante) >= 0.01
              ? ` ${reais(Math.abs(maoObraNoRestante))} ficou repartido pela receita: ficha sem setor ou lançamento sem pessoa.` : ''} Para conferir: <IrPara para={LINK_MAO_OBRA}>Financeiro › DRE & Resultado › Mão de obra</IrPara>.</span>}
          />
        ) : null}
        {erro ? <Alert type="error" showIcon message="Não consegui atualizar" description={erro.message} /> : null}
      </Space>

      <div style={{ opacity: carregando ? 0.5 : 1, transition: 'opacity .2s' }}>
        <Text strong>{rotuloSetor ? `${rotuloLinhaAtual} de ${rotuloSetor}` : `${rotuloLinhaAtual} por setor`}</Text>
        <Text type="secondary">{rotuloSetor ? ' (prejuízo desce abaixo do zero)' : ' (prejuízo desce abaixo do zero; a linha cheia é o total da empresa, igual à DRE)'}</Text>
        <div style={{ height: 'clamp(300px, calc(100vh - 420px), 520px)', marginTop: 8 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={foco.linhas} stackOffset="sign" margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
              <CartesianGrid stroke="#eee" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={reaisCurto} width={56} />
              <Tooltip content={(p: any) => (
                <TooltipEmpilhado active={p.active} payload={p.payload} label={p.label} rotuloTotal="Total da empresa" umaBarra={setorEf != null} />
              )} />
              {nSeries >= 2 ? <Legend wrapperStyle={{ fontSize: 12 }} /> : null}
              <ReferenceLine y={0} stroke="#8c8c8c" />
              {(setorEf ? [setorEf] : setores).map((s) => {
                const m = metaSetor(s);
                return <Bar key={s} dataKey={s} name={m.rotulo} stackId="lucro" fill={m.cor} stroke="#fff" strokeWidth={2} maxBarSize={56} />;
              })}
              {setorEf ? null : <Line type="monotone" dataKey="total" name="Total da empresa" stroke="#262626" strokeWidth={2} dot={{ r: 3 }} />}
              {tendencia ? (
                <Line
                  type="linear" dataKey="tendencia" name={`Tendência (${tendenciaTexto(tendencia.porMes)})`}
                  stroke={COR_TENDENCIA} strokeDasharray={TRACO_TENDENCIA} strokeWidth={2} dot={false} activeDot={false}
                  legendType="plainline" isAnimationActive={false}
                />
              ) : null}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        {avisos.abertos.length && modo === 'mes' ? <Text type="secondary">* mês em andamento.</Text> : null}
      </div>

      <div style={{ marginTop: 24, opacity: carregando ? 0.5 : 1, transition: 'opacity .2s' }}>
        {setorEf ? (
          <>
            <Text strong>DRE de {rotuloSetor}, mês a mês</Text>
            <Text type="secondary"> (o período é a soma das DREs de cada mês)</Text>
          </>
        ) : (
          <>
            <Text strong>DRE por setor, no período todo</Text>
            <Text type="secondary"> (o total é a soma das DREs de cada mês; o imposto estimado é calculado mês a mês, então pode diferir um pouco da DRE trimestral)</Text>
          </>
        )}
        <div>{botaoCategorias}</div>
        <Table
          size="small" pagination={false} tableLayout="fixed"
          scroll={{ x: LARG_PRIMEIRA + LARG_COL * (setorEf ? dados.meses.length + 1 : setores.length + 1) }}
          expandable={{ expandedRowKeys: abertas, onExpandedRowsChange: (k) => setAbertas([...k]) }}
          columns={setorEf ? colunasDoSetor : colunas} dataSource={setorEf ? linhasDoSetor : linhasTabela}
          onRow={(r) => ({ style: r.destaque ? { fontWeight: 600, background: '#fafafa' } : r.margem ? { fontStyle: 'italic', color: '#595959' } : undefined })}
        />
      </div>

      <ModalComoCalculado alvo={alvo} onFechar={() => setAlvo(null)} />

      {(pendenciasComItem.length || aceitesRegistrados.length) ? (
        <div style={{ marginTop: 24 }}>
          <Text strong>O que conferir</Text>
          <Collapse
            style={{ marginTop: 8 }}
            items={[...pendenciasComItem.map((p) => {
              const itens = itensDaPendencia(dados.pendencias[p.chave]);
              const n = contagemPendencia(dados.pendencias[p.chave]);
              return {
                key: p.chave,
                label: `${p.titulo} (${n})`,
                children: (
                  <div>
                    <div style={{ marginBottom: 8 }}>{p.onde}</div>
                    {itens.length && (p.chave === 'custos_sem_setor' || p.chave === 'custos_sem_casamento') ? (
                      <TabelaItensSetorCusto itens={itens} podeAceitar={podeAceitar} onAceitar={aceitar} />
                    ) : itens.length ? (
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {itens.slice(0, 15).map((it, i) => <li key={i}>{rotuloItem(it)}</li>)}
                      </ul>
                    ) : null}
                    {n > 15 ? <Text type="secondary">e mais {n - 15}.</Text> : null}
                  </div>
                ),
              };
            }), ...(aceitesRegistrados.length ? [{
              key: 'aceites',
              label: `Aceites registrados: custos que seguem o contrato por decisão sua (${aceitesRegistrados.length})`,
              children: <TabelaAceites aceites={aceitesRegistrados} podeAceitar={podeAceitar} onDesfazer={desfazerAceite} />,
            }] : [])]}
          />
        </div>
      ) : null}
    </div>
  );
}
